import { useState, useEffect, useCallback, useMemo } from 'react';
import {getAllClaimsForAdmin, getAdminDashboardStats,
        processClaimAction, bulkProcessClaims } from '../service/adminService.mjs';
import { secureLogger } from '../../utils/secureLogger.mjs';



const useAdminDashboard = () => {
  // Data states
  const [allClaims, setAllClaims] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [processingClaims, setProcessingClaims] = useState(new Set());
  
  // Filter states
  // Defaults kept in one place so "Clear filters" and the initial state can
  // never drift apart.
  const defaultFilters = () => ({
    status: '',
    month: '',
    year: new Date().getFullYear().toString(),
    claimType: ''
  });

  const [filters, setFilters] = useState(defaultFilters);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Selection states for bulk operations
  const [selectedClaims, setSelectedClaims] = useState(new Set());

  // Computed values - filtered claims based on current filters
  const filteredClaims = useMemo(() => {
    let filtered = [...allClaims];

    // Belt and braces. The admin API no longer returns drafts at all
    // (adminController.getAllClaimsForAdmin), so this should never remove
    // anything - it is kept so a future change to the endpoint cannot quietly
    // put other people's unsubmitted claims back on an admin's screen.
    filtered = filtered.filter(claim => claim.status !== 'draft');
    
    // Apply client-side filters
    if (filters.status && filters.status !== '') {
      filtered = filtered.filter(claim => claim.status === filters.status);
    }
    
    if (filters.claimType && filters.claimType !== '') {
      filtered = filtered.filter(claim => claim.claimType === filters.claimType);
    }

    // Apply date filters if needed
    if (filters.month && filters.month !== '') {
      filtered = filtered.filter(claim => {
        const claimDate = new Date(claim.createdAt);
        return claimDate.getMonth() + 1 === parseInt(filters.month);
      });
    }

    if (filters.year && filters.year !== '') {
      filtered = filtered.filter(claim => {
        const claimDate = new Date(claim.createdAt);
        return claimDate.getFullYear() === parseInt(filters.year);
      });
    }
    
    return filtered;
  }, [allClaims, filters]);

  // Paginated claims for display
  const paginatedClaims = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredClaims.slice(startIndex, endIndex);
  }, [filteredClaims, currentPage, itemsPerPage]);

  // Total pages
  const totalPages = useMemo(() => {
    return Math.ceil(filteredClaims.length / itemsPerPage);
  }, [filteredClaims.length, itemsPerPage]);

  // Summary statistics based on ALL claims (not filtered)
  const summaryStats = useMemo(() => {
    const stats = {
      total: allClaims.length,
      submitted: 0,
      approved: 0,
      rejected: 0,
      returned: 0,
      received: 0,
      totalAmount: 0
    };

    allClaims.forEach(claim => {
      if (claim.status) {
        stats[claim.status] = (stats[claim.status] || 0) + 1;
        // Handle 'submitted' as 'received' for display
        if (claim.status === 'submitted') {
          stats.received = (stats.received || 0) + 1;
        }
      }
      
      const amount = claim.totalAmount || claim.totalClaim || claim.totalUsdAmount || 0;
      stats.totalAmount += parseFloat(amount) || 0;
    });

    return stats;
  }, [allClaims]);

  // Filtered summary statistics based on current filters
  const filteredSummaryStats = useMemo(() => {
    const stats = {
      total: filteredClaims.length,
      submitted: 0,
      approved: 0,
      rejected: 0,
      returned: 0,
      received: 0,
      totalAmount: 0
    };

    filteredClaims.forEach(claim => {
      if (claim.status) {
        stats[claim.status] = (stats[claim.status] || 0) + 1;
        // Handle 'submitted' as 'received' for display
        if (claim.status === 'submitted') {
          stats.received = (stats.received || 0) + 1;
        }
      }
      
      const amount = claim.totalAmount || claim.totalClaim || claim.totalUsdAmount || 0;
      stats.totalAmount += parseFloat(amount) || 0;
    });

    return stats;
  }, [filteredClaims]);

  // Fetch claims function
  const fetchClaims = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    
    setError(null);
    
    try {
      // Get all claims with current filters applied on server side
      const claimsResponse = await getAllClaimsForAdmin(filters);
      const claimsData = claimsResponse.data || claimsResponse;
      
      // Try to get dashboard stats if available
      let statsData = {};
      try {
        const statsResponse = await getAdminDashboardStats(filters);
        statsData = statsResponse.data || statsResponse;
      } catch (statsError) {
        secureLogger.error('Dashboard stats not available', { status: statsError.response?.status });
      }
      
      setAllClaims(Array.isArray(claimsData) ? claimsData : []);
      setDashboardStats(statsData);
      
    } catch (err) {
      secureLogger.error('Error fetching admin data', { status: err.response?.status });
      setError(err.message || 'Failed to fetch claims data');
      setAllClaims([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters]);

  // Process individual claim
  const processIndividualClaim = useCallback(async (claimType, claimId, actionData) => {
    setProcessingClaims(prev => new Set([...prev, claimId]));
    
    try {
      const result = await processClaimAction(claimType, claimId, actionData);
      
      // Update the claim in local state
      setAllClaims(prev => prev.map(claim => 
        claim._id === claimId 
          ? { ...claim, status: actionData.action, adminComments: actionData.comments }
          : claim
      ));
      
      return result;
    } catch (error) {
      secureLogger.error('Error processing claim', { status: error.response?.status });
      throw error;
    } finally {
      setProcessingClaims(prev => {
        const newSet = new Set(prev);
        newSet.delete(claimId);
        return newSet;
      });
    }
  }, []);

  // Bulk-deleting drafts was removed with the admin draft view: drafts are no
  // longer returned to an admin, so there is nothing here to select or delete.
  // The backend route (DELETE /admin/drafts/bulk) still exists but is no longer
  // reachable from the UI.

  // Bulk process claims
  const processBulkClaims = useCallback(async (action, comments = '') => {
    if (selectedClaims.size === 0) {
      throw new Error('No claims selected');
    }

    const claimIds = Array.from(selectedClaims);
    setProcessingClaims(prev => new Set([...prev, ...claimIds]));
    
    try {
      const result = await bulkProcessClaims(claimIds, action, comments);
      
      // Update claims in local state
      setAllClaims(prev => prev.map(claim => 
        selectedClaims.has(claim._id)
          ? { ...claim, status: action, adminComments: comments }
          : claim
      ));
      
      // Clear selection
      setSelectedClaims(new Set());
      
      return result;
    } catch (error) {
      secureLogger.error('Error bulk processing claims', { status: error.response?.status });
      throw error;
    } finally {
      setProcessingClaims(prev => {
        const newSet = new Set(prev);
        claimIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    }
  }, [selectedClaims]);

  // Selection functions
  const toggleClaimSelection = useCallback((claimId) => {
    setSelectedClaims(prev => {
      const newSet = new Set(prev);
      if (newSet.has(claimId)) {
        newSet.delete(claimId);
      } else {
        newSet.add(claimId);
      }
      return newSet;
    });
  }, []);

  const selectAllClaims = useCallback(() => {
    const selectableClaimIds = paginatedClaims
      .filter(claim => claim.status === 'submitted')
      .map(claim => claim._id);
    setSelectedClaims(new Set(selectableClaimIds));
  }, [paginatedClaims]);

  const clearSelection = useCallback(() => {
    setSelectedClaims(new Set());
  }, []);

  // Update individual filter
  const updateFilter = useCallback((key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
    // Reset to first page when filters change
    setCurrentPage(1);
  }, []);

  // Reset every filter back to its default.
  const clearFilters = useCallback(() => {
    setFilters(defaultFilters());
    setCurrentPage(1);
  }, []);

  // Update multiple filters at once
  const updateFilters = useCallback((newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
    setCurrentPage(1);
  }, []);

  // Clear all filters

  // Refresh function that can be called manually
  const refreshClaims = useCallback(() => {
    fetchClaims(false);
  }, [fetchClaims]);

  // Search function
  const searchClaims = useCallback((searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return filteredClaims;
    }
    
    const term = searchTerm.toLowerCase();
    return filteredClaims.filter(claim => {
      const employeeName = claim.user ? 
        `${claim.user.firstName} ${claim.user.lastName}`.toLowerCase() : '';
      const claimId = claim._id.toLowerCase();
      const claimType = claim.claimType.toLowerCase();
      
      return employeeName.includes(term) || 
             claimId.includes(term) || 
             claimType.includes(term);
    });
  }, [filteredClaims]);

  // Pagination functions
  const goToPage = useCallback((page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  }, [totalPages]);

  const goToNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  }, [currentPage, totalPages]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  // Update items per page
  const updateItemsPerPage = useCallback((newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  return {
    // Data
    claims: paginatedClaims,
    allClaims: filteredClaims,
    rawClaims: allClaims,
    dashboardStats,
    summaryStats,
    filteredSummaryStats,
    
    // State
    loading,
    error,
    refreshing,
    processingClaims,
    
    // Filters
    filters,
    updateFilter,
    updateFilters,
    clearFilters,
    
    // Pagination
    currentPage,
    totalPages,
    itemsPerPage,
    updateItemsPerPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    
    // Selection
    selectedClaims,
    toggleClaimSelection,
    selectAllClaims,
    clearSelection,
    
    // Actions
    refreshClaims,
    searchClaims,
    processIndividualClaim,
    processBulkClaims,
    
    // Utility
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
    totalClaims: filteredClaims.length,
    totalAllClaims: allClaims.length,
    hasSelectedClaims: selectedClaims.size > 0
  };
};

export default useAdminDashboard;

